<template>
  <div>
    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
    </div>

    <!-- No analysis yet -->
    <div v-else-if="!analysis" class="space-y-6 py-6">
      <div class="text-center">
        <h3 class="text-lg font-heading font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Semantic Analysis
        </h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Analyze topic coverage and internal link opportunities.
        </p>
        <p v-if="runError" class="text-sm text-red-600 dark:text-red-400 mb-4">
          {{ runError }}
        </p>
        <button @click="handleRun" :disabled="!canRun" class="btn btn-primary disabled:opacity-50">
          Run Semantic Analysis
        </button>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
            Input Reliability
          </p>
          <p class="text-sm text-gray-900 dark:text-gray-100 mt-1">
            {{ reliabilitySummary }}
          </p>
        </div>
        <div class="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
            Competitor Coverage
          </p>
          <p class="text-sm text-gray-900 dark:text-gray-100 mt-1">
            {{ competitorStatus?.message || 'Competitor readiness has not been checked yet.' }}
          </p>
        </div>
      </div>
      <div v-if="readinessWarnings.length" class="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
        <p v-for="warning in readinessWarnings" :key="warning" class="text-sm text-amber-800 dark:text-amber-300">
          {{ warning }}
        </p>
      </div>
    </div>

    <!-- Running -->
    <div v-else-if="analysis.status !== 'completed' && analysis.status !== 'failed'" class="text-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4" />
      <p class="text-sm text-gray-600 dark:text-gray-400">
        {{ progressMessage || 'Analyzing semantic structure...' }}
      </p>
    </div>

    <!-- Failed -->
    <div v-else-if="analysis.status === 'failed'" class="text-center py-12">
      <p class="text-sm text-red-600 dark:text-red-400 mb-4">
        {{ analysis.error_message || 'Analysis failed. Please try again.' }}
      </p>
      <button @click="handleRun" :disabled="!canRun" class="btn btn-primary disabled:opacity-50">
        Retry
      </button>
    </div>

    <!-- Results -->
    <div v-else class="space-y-6">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
            Input Reliability
          </p>
          <p class="text-sm text-gray-900 dark:text-gray-100 mt-1">
            {{ reliabilitySummary }}
          </p>
        </div>
        <div class="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
            Competitor Coverage
          </p>
          <p class="text-sm text-gray-900 dark:text-gray-100 mt-1">
            {{ competitorStatus?.message || 'Competitor coverage verified.' }}
          </p>
        </div>
      </div>
      <div v-if="reliabilityWarnings.length" class="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
        <p v-for="warning in reliabilityWarnings" :key="warning" class="text-sm text-amber-800 dark:text-amber-300">
          {{ warning }}
        </p>
      </div>

      <!-- Topic Coverage Matrix -->
      <div class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <div class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800">
          <span class="font-medium text-gray-900 dark:text-gray-100">
            Topic Coverage Matrix
          </span>
          <span class="text-sm text-gray-500 dark:text-gray-400">
            Est. word impact: +{{ totalWordImpact }}
          </span>
        </div>
        <div class="p-4 overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs text-gray-500 dark:text-gray-400 uppercase bg-gray-50 dark:bg-gray-900/50">
                <th class="pb-2 pr-4">
                  Select
                </th>
                <th class="pb-2 pr-4">
                  Topic
                </th>
                <th class="pb-2 pr-4">
                  Priority
                </th>
                <th class="pb-2 pr-4">
                  Words
                </th>
                <th class="pb-2 pr-4">
                  Page
                </th>
                <th class="pb-2 pr-4">
                  Reason
                </th>
                <th v-for="comp in competitorColumns" :key="comp" class="pb-2 pr-4">
                  {{ comp }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="topic in topicMatrix"
                :key="topic.topic"
                :class="topicRowClass(topic)"
                class="border-t border-gray-100 dark:border-gray-800">
                <td class="py-2 pr-4">
                  <input
                    type="checkbox"
                    :checked="isTopicSelected(topic)"
                    @change="toggleTopicSelection(topic)"
                    class="rounded border-gray-300 dark:border-gray-600 text-primary-500 focus:ring-primary-500">
                </td>
                <td class="py-2 pr-4 text-gray-900 dark:text-gray-100">
                  {{ topic.topic }}
                </td>
                <td class="py-2 pr-4">
                  <span :class="topicPriorityClass(topic.priority)" class="px-2 py-0.5 rounded-lg text-xs font-medium">
                    {{ topic.priority }}
                  </span>
                </td>
                <td class="py-2 pr-4 text-gray-600 dark:text-gray-400">
                  {{ topic.estimated_words || '—' }}
                </td>
                <td class="py-2 pr-4">
                  <span v-if="topic.page_has" class="text-green-500">&#10003;</span>
                  <span v-else class="text-red-400">&#10007;</span>
                </td>
                <td class="py-2 pr-4 text-gray-600 dark:text-gray-400 min-w-64">
                  {{ topic.reason || 'Reason unavailable.' }}
                </td>
                <td v-for="comp in competitorColumns" :key="comp" class="py-2 pr-4">
                  <span v-if="topic.competitors?.[comp]" class="text-green-500">&#10003;</span>
                  <span v-else class="text-gray-300 dark:text-gray-600">&#10007;</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Internal Link Recommendations -->
      <div v-if="linkRecommendations.length" class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <button @click="expandedLinks = !expandedLinks" class="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800">
          <span class="font-medium text-gray-900 dark:text-gray-100">
            Internal Link Recommendations ({{ linkRecommendations.length }})
          </span>
          <span class="text-gray-400">{{ expandedLinks ? '−' : '+' }}</span>
        </button>
        <div v-if="expandedLinks" class="p-4 space-y-3">
          <div v-for="(link, i) in linkRecommendations" :key="i" class="flex items-start gap-3 text-sm border-t border-gray-100 dark:border-gray-800 first:border-0 py-2 first:pt-0">
            <div class="flex-1">
              <p class="text-gray-900 dark:text-gray-100">
                {{ link.anchor_text }}
              </p>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {{ link.target_url }} &mdash; {{ link.reasoning }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Approve button -->
      <div class="flex justify-end">
        <button @click="handleApprove" class="btn btn-primary">
          {{ analysis.approved ? 'Update' : 'Approve' }} Topic Plan ({{ selectedTopicCount }} topics)
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import consumer from '~/channels/consumer'
import { runSemanticAnalysis, getSemanticAnalysis, approveTopicPlan } from '~/javascript/api/semantic_analysis.js'

const props = defineProps({
  pageId: { type: String, required: true }
})

const emit = defineEmits(['approved'])

let subscription = null
let pollTimer = null

const loading = ref(true)
const analysis = ref(null)
const readiness = ref(null)
const progressMessage = ref('')
const runError = ref('')
const expandedLinks = ref(false)
const topicSelections = ref({})

const topicMatrix = computed(() => analysis.value?.topic_matrix || [])
const competitorColumns = computed(() => analysis.value?.competitor_columns || [])
const linkRecommendations = computed(() => analysis.value?.link_recommendations || [])
const effectiveReadiness = computed(() => analysis.value?.readiness || readiness.value || {})
const competitorStatus = computed(() => effectiveReadiness.value.competitor_status || null)
const readinessWarnings = computed(() => effectiveReadiness.value.blocking_messages || [])
const reliabilityWarnings = computed(() => analysis.value?.reliability_warnings || effectiveReadiness.value.warnings || [])
const canRun = computed(() => effectiveReadiness.value.can_run === true)
const reliabilitySummary = computed(() => {
  const reliability = analysis.value?.input_reliability || effectiveReadiness.value.input_reliability
  if (reliability === 'verified') return 'Verified inputs from approved competitors, strategy, counting, and extracted content.'
  if (reliability === 'warning') return 'Inputs are usable, but topic coverage has warnings.'
  if (readinessWarnings.value.length) return readinessWarnings.value[0]
  return 'Topic input readiness has not been checked yet.'
})

const isTopicSelected = (topic) => {
  if (topicSelections.value[topic.topic] !== undefined) return topicSelections.value[topic.topic]
  return topic.priority === 'MUST_ADD' || topic.priority === 'SHOULD_ADD'
}

const selectedTopicCount = computed(() => topicMatrix.value.filter((t) => isTopicSelected(t)).length)

const totalWordImpact = computed(() => topicMatrix.value
  .filter((t) => isTopicSelected(t) && !t.page_has)
  .reduce((sum, t) => sum + (t.estimated_words || 0), 0))

const toggleTopicSelection = (topic) => {
  topicSelections.value[topic.topic] = !isTopicSelected(topic)
}

const initializeTopicSelections = (semanticAnalysis) => {
  const selections = semanticAnalysis?.user_topic_selections || []
  if (!semanticAnalysis?.approved && selections.length === 0) {
    topicSelections.value = {}
    return
  }

  const selectedTopics = new Set(selections.map((selection) => {
    if (!selection) return null
    if (typeof selection === 'string') return selection
    return selection.topic || selection.topic_name
  }).filter(Boolean))

  const nextSelections = {}
  const topics = semanticAnalysis?.topic_matrix || []
  topics.forEach((topic) => {
    nextSelections[topic.topic] = selectedTopics.has(topic.topic)
  })
  topicSelections.value = nextSelections
}

const topicRowClass = (topic) => {
  if (topic.priority === 'MUST_ADD') return 'bg-red-50/50 dark:bg-red-900/10'
  if (topic.priority === 'SHOULD_ADD') return 'bg-amber-50/50 dark:bg-amber-900/10'
  return ''
}

const topicPriorityClass = (priority) => {
  const map = {
    MUST_ADD: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
    SHOULD_ADD: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
    COULD_ADD: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
  }
  return map[priority] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
}

const fetchAnalysis = async() => {
  try {
    loading.value = true
    const response = await getSemanticAnalysis(props.pageId)
    analysis.value = response.data.semantic_analysis
    readiness.value = response.data.readiness || analysis.value?.readiness || null
    initializeTopicSelections(analysis.value)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error fetching semantic analysis:', e)
  } finally {
    loading.value = false
  }
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

function startPolling() {
  stopPolling()
  pollTimer = setInterval(async() => {
    try {
      const response = await getSemanticAnalysis(props.pageId)
      const a = response.data.semantic_analysis
      readiness.value = response.data.readiness || readiness.value
      if (a && (a.status === 'completed' || a.status === 'failed')) {
        analysis.value = a
        initializeTopicSelections(a)
        stopPolling()
      }
    } catch (_e) { /* ignore polling errors */ }
  }, 5000)
}

const subscribeToChannel = () => {
  if (subscription) subscription.unsubscribe()
  subscription = consumer.subscriptions.create(
    { channel: 'SemanticAnalysisChannel', page_id: props.pageId },
    {
      received(data) {
        progressMessage.value = data.message || ''
        if (data.step === 'completed' || data.step === 'failed') {
          stopPolling()
          fetchAnalysis()
        }
      }
    }
  )
}

const handleRun = async() => {
  try {
    runError.value = ''
    const response = await runSemanticAnalysis(props.pageId)
    readiness.value = response.data.readiness || readiness.value
    subscribeToChannel()
    analysis.value = { status: 'analyzing' }
    topicSelections.value = {}
    loading.value = false
    startPolling()
  } catch (e) {
    runError.value = e.response?.data?.message || 'Failed to start semantic analysis.'
    readiness.value = e.response?.data?.readiness || readiness.value
    // eslint-disable-next-line no-console
    console.error('Failed to start semantic analysis:', e)
  }
}

const handleApprove = async() => {
  try {
    const selections = topicMatrix.value
      .filter((t) => isTopicSelected(t))
      .map((t) => ({ topic: t.topic, priority: t.priority }))
    await approveTopicPlan(props.pageId, selections)
    await fetchAnalysis()
    emit('approved')
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to approve topic plan:', e)
  }
}

onMounted(async() => {
  await fetchAnalysis()
  subscribeToChannel()
  if (analysis.value && analysis.value.status !== 'completed' && analysis.value.status !== 'failed') {
    startPolling()
  }
})

onUnmounted(() => {
  subscription?.unsubscribe()
  stopPolling()
})
</script>
